/** Minimal frame-source abstraction so tweens don't depend on PixiJS or `requestAnimationFrame` directly. */
export interface Ticker {
  add(fn: (deltaMS: number) => void): () => void;
}

export type Easing = (t: number) => number;

export const linear: Easing = (t) => t;
export const easeOutQuad: Easing = (t) => 1 - (1 - t) * (1 - t);

type NumericKeys<T> = { [K in keyof T]: T[K] extends number ? K : never }[keyof T];

/** Flip `requested` to jump a running tween straight to its end value (e.g. player skipped a cutscene). */
export interface SkipSignal {
  requested: boolean;
}

/** Interpolates the numeric fields named in `to` on `target` over `durationMs`. Mutates `target` in place. */
export function tween<T extends object>(
  ticker: Ticker,
  target: T,
  to: Partial<Pick<T, NumericKeys<T>>>,
  durationMs: number,
  easing: Easing = linear,
  skip?: SkipSignal,
): Promise<void> {
  const from = {} as Record<string, number>;
  for (const key of Object.keys(to)) from[key] = (target as Record<string, number>)[key] ?? 0;

  return new Promise((resolve) => {
    const finish = () => {
      Object.assign(target, to);
      unsubscribe();
      resolve();
    };
    if (durationMs <= 0) {
      Object.assign(target, to);
      resolve();
      return;
    }
    let elapsed = 0;
    const unsubscribe = ticker.add((deltaMS) => {
      if (skip?.requested) {
        finish();
        return;
      }
      elapsed += deltaMS;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easing(t);
      for (const key of Object.keys(to)) {
        const a = from[key] ?? 0;
        const b = (to as Record<string, number>)[key] ?? a;
        (target as Record<string, number>)[key] = a + (b - a) * eased;
      }
      if (t >= 1) {
        unsubscribe();
        resolve();
      }
    });
  });
}
